import { useState, useEffect } from "react";
import { addFunds, fetchAllUsers } from "../services/api";

const WalletManagement = () => {
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [amount, setAmount] = useState("");
  const [transactionType, setTransactionType] = useState("add");
  const [message, setMessage] = useState({ text: "", type: "" });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const data = await fetchAllUsers();
        setUsers(data);
      } catch (error) {
        setMessage({
          text: `Error fetching users: ${error.message}`,
          type: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ text: "", type: "" });

    if (!selectedUserId || !amount) {
      setMessage({
        text: "Please select a user and enter an amount.",
        type: "error",
      });
      return;
    }

    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      setMessage({ text: "Please enter a valid positive amount.", type: "error" });
      return;
    }

    const user = users.find((u) => u.id === parseInt(selectedUserId));
    if (!user) {
      setMessage({ text: "Selected user not found.", type: "error" });
      return;
    }

    if (transactionType === "deduct" && user.balance < amountNum) {
      setMessage({
        text: "Insufficient balance for deduction.",
        type: "error",
      });
      return;
    }

    setSubmitting(true);

    try {
      let result;
      if (transactionType === "add") {
        result = await addFunds({ user_id: selectedUserId, amount: amountNum });
      }

      const updatedBalance =
        result?.new_balance ??
        user.balance +
        (transactionType === "add" ? amountNum : -amountNum);

      setUsers((prevUsers) =>
        prevUsers.map((u) =>
          u.id === user.id ? { ...u, balance: updatedBalance } : u
        )
      );

      setMessage({
        text: `Successfully ${transactionType === "add" ? "added" : "deducted"
          } $${amountNum.toFixed(2)} to ${user.name}'s wallet. New balance: $${updatedBalance.toFixed(
            2
          )}`,
        type: "success",
      });

      setAmount("");
      setSelectedUserId("");
    } catch (error) {
      setMessage({
        text: `Transaction failed: ${error.message}`,
        type: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="text-center py-5">Loading users...</div>;
  }

  return (
    <div className="container-fuild h-100">
      <div className="wallet-management-wrapper">
        <div className="theme-card-white w-50">
          <div className="theme-card-header">
            <h5 className="card-head">Wallet Management</h5>
          </div>
          <form onSubmit={handleSubmit} className="wallet-form">
            {/* Message */}
            {message.text && (
              <div
                className={`alert mt-4 ${message.type === "error" ? "alert-danger" : "alert-success"
                  }`}
                role="alert"
              >
                {message.text}
              </div>
            )}
            {/* User Select */}
            <div>
              <label htmlFor="user" className="form-label">
                Select User
              </label>
              <select
                id="user"
                className="form-select"
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                disabled={submitting}
              >
                <option value="">Select a user</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name} (${user.balance.toFixed(2)})
                  </option>
                ))}
              </select>
            </div>

            {/* Transaction Type */}
            <div>
              <label
                htmlFor="transactionType"
                className="form-label">
                Transaction Type
              </label>
              <select
                id="transactionType"
                className="form-select"
                value={transactionType}
                onChange={(e) => setTransactionType(e.target.value)}
                disabled={submitting}
              >
                <option value="add">Add Money</option>
                <option value="deduct">Deduct Money</option>
              </select>
              <small className="theme-color-text pt-2 text-xs font-500 d-block">
                Note: Deduct Money feature is under development.
              </small>
            </div>

            {/* Amount Input */}
            <div>
              <label htmlFor="amount" className="form-label">
                Amount
              </label>
              <input
                type="number"
                id="amount"
                className="form-control"
                placeholder="Enter amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                step="0.01"
                min="0"
                disabled={submitting}
              />
            </div>
            <div className="pt-3">
              <button type="submit" className="theme-btn" disabled={submitting}>
                {submitting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Processing...
                  </>
                ) : (
                  "Process Transaction"
                )}
              </button>
            </div>

            {/* Submit */}
          </form>
        </div>
      </div>
    </div>
  );
};

export default WalletManagement;
